import { Link } from 'react-router-dom';
import {
  CalendarCheck,
  Phone,
  CheckCircle,
  ArrowRight,
  CaretRight,
  MapPin,
  UsersThree,
  Globe,
} from '@phosphor-icons/react';
import { BRAND, COVERAGE } from '../lib/brand';

/**
 * ServiceDetailLayout — Shared shell for every individual service page.
 *
 * Props:
 *  - eyebrow:        e.g., "Document Service"
 *  - title:          main H1 (string or JSX)
 *  - titleAccent:    italicized trailing word (string)
 *  - subtitle:       hero paragraph
 *  - heroImage:      url for hero background
 *  - intro:          { kicker, heading, body[] (array of paragraphs), pullQuote? }
 *  - bullets:        array of { title, description } — "What's included"
 *  - extraSection:   optional JSX block (used by Estate Planning page)
 *  - faqs:           array of { q, a }
 *  - relatedSlugs:   array of slug strings to render related-service links
 *  - servicesIndex:  array of { slug, name, blurb } — full registry for related links
 */
export const ServiceDetailLayout = ({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  heroImage,
  intro,
  bullets,
  extraSection,
  faqs,
  relatedSlugs = [],
  servicesIndex = [],
}) => {
  const related = relatedSlugs
    .map((s) => servicesIndex.find((x) => x.slug === s))
    .filter(Boolean);

  return (
    <main data-testid="service-detail-page">
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0000]/95 via-[#800000]/65 to-[#800000]/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-white/70 mb-6 tracking-wide">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            <CaretRight size={10} weight="bold" />
            <Link to="/services" className="hover:text-[#D4AF37] transition-colors">Services</Link>
            <CaretRight size={10} weight="bold" />
            <span className="text-[#D4AF37]">{eyebrow}</span>
          </nav>

          <div className="inline-flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">
              {eyebrow}
            </span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.05] mb-6 max-w-3xl drop-shadow-2xl">
            {title}{' '}
            {titleAccent && <span className="italic gold-shimmer">{titleAccent}</span>}
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed mb-8 drop-shadow">
            {subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/booking"
              data-testid="service-detail-book-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-3.5 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-2xl"
            >
              <CalendarCheck weight="bold" size={18} />
              Book This Service
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-3 border border-white/60 backdrop-blur-sm bg-white/10 text-white hover:bg-white hover:text-[#800000] transition-all px-7 py-3.5 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={18} />
              {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="flex items-center gap-3 text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            {intro.kicker}
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-8 leading-tight">
            {intro.heading}
          </h2>
          <div className="space-y-5 text-[#1A1A1A] leading-[1.85] text-base max-w-3xl">
            {intro.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {intro.pullQuote && (
            <div className="mt-10 pl-6 border-l-[3px] border-[#D4AF37] max-w-3xl">
              <p className="font-serif-display italic text-xl sm:text-2xl text-[#800000] leading-relaxed">
                "{intro.pullQuote.text}"
              </p>
              {intro.pullQuote.attribution && (
                <p className="text-xs text-[#5C5C5C] mt-3 tracking-[0.2em] uppercase">
                  — {intro.pullQuote.attribution}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Bullets / What's included */}
      {bullets && bullets.length > 0 && (
        <section className="py-20 bg-[#FBF8F1]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14 max-w-2xl mx-auto">
              <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                What's Included
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
                Done right, <span className="italic">the first time.</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {bullets.map((b, i) => (
                <div
                  key={i}
                  data-testid={`service-bullet-${i}`}
                  className="bg-white border border-[#E8E2D5] hover:border-[#D4AF37]/50 hover:shadow-xl rounded-sm p-7 transition-all"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <CheckCircle weight="fill" size={22} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <h3 className="font-serif-display text-lg font-semibold text-[#800000] leading-tight">
                      {b.title}
                    </h3>
                  </div>
                  <p className="text-[#5C5C5C] text-sm leading-relaxed pl-9">
                    {b.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Optional unique block (Estate page uses this for the 7 documents) */}
      {extraSection}

      {/* FAQs */}
      {faqs && faqs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                Frequently Asked
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
                Good questions. <span className="italic">Honest answers.</span>
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  data-testid={`service-faq-${i}`}
                  className="group bg-[#FBF8F1] border border-[#E8E2D5] rounded-sm hover:border-[#D4AF37]/50 transition-colors"
                >
                  <summary className="cursor-pointer list-none p-6 flex items-center justify-between gap-6">
                    <span className="font-serif-display text-lg sm:text-xl text-[#800000] font-semibold pr-4">
                      {f.q}
                    </span>
                    <CaretRight
                      weight="bold"
                      size={18}
                      className="text-[#D4AF37] flex-shrink-0 transition-transform group-open:rotate-90"
                    />
                  </summary>
                  <div className="px-6 pb-6 -mt-1 text-[#3C3C3C] leading-[1.85] text-sm sm:text-[15px]">
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related services */}
      {related.length > 0 && (
        <section className="py-20 bg-[#FBF8F1]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                Pairs Well With
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
                Other ways India can <span className="italic">help.</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r, i) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  data-testid={`related-service-${i}`}
                  className="group bg-white border border-[#E8E2D5] hover:border-[#800000]/30 hover:shadow-xl rounded-sm p-7 transition-all block"
                >
                  <h3 className="font-serif-display text-lg font-semibold text-[#800000] mb-2 leading-tight">
                    {r.name}
                  </h3>
                  <p className="text-[#5C5C5C] text-sm leading-relaxed mb-4">
                    {r.blurb}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#800000] group-hover:text-[#D4AF37] transition-colors">
                    Learn More <ArrowRight weight="bold" size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA — 3-tier coverage */}
      <section className="py-20 bg-[#800000] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              Service Coverage
            </p>
            <h3 className="font-serif-display text-3xl sm:text-4xl font-semibold text-white leading-tight">
              {COVERAGE.headline.split(' · ').map((word, i, arr) => (
                <span key={i}>
                  <span className={i === 1 ? 'italic gold-shimmer' : ''}>{word}</span>
                  {i < arr.length - 1 && <span className="text-[#D4AF37] mx-2">·</span>}
                </span>
              ))}
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {COVERAGE.tiers.map((tier, i) => {
              const Icon = i === 0 ? MapPin : i === 1 ? UsersThree : Globe;
              return (
                <div
                  key={i}
                  data-testid={`service-coverage-tier-${i}`}
                  className="bg-white/[0.06] backdrop-blur-sm border border-white/15 rounded-sm p-5"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Icon weight="duotone" size={22} className="text-[#D4AF37]" />
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-bold">
                      {tier.label}
                    </p>
                  </div>
                  <p className="font-serif-display text-lg text-white font-semibold leading-tight">
                    {tier.area}
                  </p>
                  <p className="text-white/65 text-xs mt-1 leading-relaxed">
                    {tier.detail}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
            <Link
              to="/booking"
              data-testid="service-final-book-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-4 rounded-sm font-semibold text-sm uppercase tracking-[0.15em]"
            >
              <CalendarCheck weight="bold" size={20} />
              Schedule Now
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#800000] transition-all px-7 py-4 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={20} />
              Call Now
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
